import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { map } from "rxjs";
import {
  FeatureFlagKey,
  FeatureFlagService,
} from "../services/feature-flag.service";

/**
 * Route guard factory that denies activation when a feature flag is off.
 * Redirects to /forbidden, same as permissionGuard and scopeGuard — one denial
 * destination for the whole app rather than a second one for this case.
 *
 * Pair it with `requiredFeatureFlag` in the route's `data.menu` so the sidebar
 * entry disappears too; the guard only stops someone typing the URL. Same
 * deliberate duplication as permissionGuard / requiredPermission — the guard is
 * a closure and cannot be introspected by RouterMenuService.
 *
 *   {
 *     path: "mail-config",
 *     canActivate: [
 *       featureFlagGuard("enableMailManagement"),
 *       permissionGuard(Permissions.MailConfigRead),
 *     ],
 *     data: {
 *       menu: {
 *         label: "Mail Management",
 *         requiredFeatureFlag: "enableMailManagement",
 *         requiredPermission: Permissions.MailConfigRead,
 *       } satisfies MenuMeta,
 *     },
 *   }
 *
 * Right after login, or on a deep link into a fresh session, the shared
 * (remote) flag values have not arrived yet and the local ones may be stale.
 * In that window the guard waits for the first remote attempt to finish —
 * bounded, see `REMOTE_FIRST_RESPONSE_WAIT_MS`; after that it decides on
 * whatever it has. Otherwise it decides synchronously.
 *
 * Flags are **UX only** — they ship in a config file a client can read and the
 * guard runs in the browser. The API must gate the same feature itself
 * (cxaconfig.GetBool on the BFF side); hiding the page does not stop anyone
 * calling the endpoint.
 */
export function featureFlagGuard(
  required: FeatureFlagKey | readonly FeatureFlagKey[],
): CanActivateFn {
  const list = Array.isArray(required)
    ? required
    : [required as FeatureFlagKey];

  return () => {
    const flags = inject(FeatureFlagService);
    const router = inject(Router);

    const decide = () =>
      flags.getAll(list) ? true : router.parseUrl("/forbidden");

    if (!flags.isRemotePending()) {
      return decide();
    }
    return flags.whenRemoteSettled().pipe(map(decide));
  };
}
